(ns dashboard.views.questionnaire.questions.view
  (:require ["react" :as react]
            [clojure.string :as str]
            [dashboard.components.breadcrumbs :refer [breadcrumbs]]
            [dashboard.components.popup-menu :as popup-menu]
            [dashboard.components.questionnaire.questionnaire-status :refer [questionnaire-status]]
            [dashboard.components.questionnaire.section-tabs :refer [section-tabs]]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.modals :as analysis.view]
            [dashboard.entity.analysis.policy-process-pairs :as process<->policy]
            [dashboard.entity.analysis.search :as analysis.search]
            [dashboard.entity.analysis.tree :as analysis.tree]
            [dashboard.entity.question.analysis-search :as question.analysis.search]
            [dashboard.entity.question.answer-scope :as answer-scope]
            [dashboard.entity.question.comparison :as comp.compare]
            [dashboard.entity.question.db :as questions.db]
            [dashboard.entity.question.profile :as question.profile]
            [dashboard.entity.questionnaire.db :as questionnaire.db]
            [dashboard.entity.questionnaire.progress-bar :as progress.bar]
            [dashboard.services.answers :as answers]
            [dashboard.services.document-list :as documents]
            [dashboard.services.question-note :as question-note]
            [dashboard.services.questionnaire-list :as questionnaire-list]
            [dashboard.services.questionnaires :as questionnaires]
            [dashboard.subs :as common-subs]
            [dashboard.ui :as ui]
            [dashboard.views.questionnaire.components.archived-list :as archives]
            [dashboard.views.questionnaire.components.inference-job-list :as inference-jobs]
            [dashboard.views.questionnaire.components.title :as title]
            [dashboard.views.questionnaire.question-lab.events :as question-lab.events]
            [dashboard.views.questionnaire.question-lab.view :as question-lab.view]
            [dashboard.views.questionnaire.questions.config :as config]
            [dashboard.views.recommendation.recommend-analysis :as recommend-analysis]
            [dashboard.views.recommendation.recommend-question :as recommend-question]
            [re-frame.core :as rf]))

(def controllers
  [{:parameters {:path [:questionnaire-id]}
    :start (fn [{{:keys [questionnaire-id]} :path}]
             (let [questionnaire-id (uuid questionnaire-id)]
               (analysis.db/get-applied-analyses false)
               (analysis.db/get-categories false)
               (analysis.db/get-structured-analyses false)
               (analysis.db/get-structured-analysis-questions false)
               (questionnaire.db/get-attachments questionnaire-id)
               (questionnaire.db/get-metadata questionnaire-id nil)
               (questionnaire.db/get-questions questionnaire-id nil)
               (questions.db/get-sections questionnaire-id)
               (questions.db/init-comparisons questionnaire-id)
               (questions.db/init-view questionnaire-id)
               (rf/dispatch [::answer-scope/get])
               (rf/dispatch [::archives/fetch-archived-list questionnaire-id])
               (rf/dispatch [::documents/get])
               (rf/dispatch [::inference-jobs/fetch-jobs questionnaire-id])
               (rf/dispatch [::inference-jobs/get-start-configuration questionnaire-id])
               (rf/dispatch [::inference-jobs/start-polling questionnaire-id])
               (rf/dispatch [::question-note/get-notes questionnaire-id])
               (rf/dispatch [::question-note/get-notes-last-seen questionnaire-id])
               (rf/dispatch [::question-note/start-polling-for-notes questionnaire-id])
               (rf/dispatch [::questionnaire-list/get])))
    :stop (fn [{{:keys [questionnaire-id]} :path}]
            (let [questionnaire-id (uuid questionnaire-id)]
              (rf/dispatch [::question-note/stop-polling-for-notes])
              (rf/dispatch [::inference-jobs/stop-polling questionnaire-id])))}])

(defn- highest-dom-parent
  [elem]
  (if (.-parentElement elem)
    (highest-dom-parent (.-parentElement elem))
    elem))

(defn scroll-fn [scrolling-elem target-scroll-top]
  (js/setTimeout
   (fn []
     (when (< (+ (.-scrollTop scrolling-elem) 4)
              target-scroll-top)
       (let [scroll-top-before (.-scrollTop scrolling-elem)]
         (-> scrolling-elem
             (aset "scrollTop" (+ (.-scrollTop scrolling-elem)
                                  4)))
         (when (not= (.-scrollTop scrolling-elem)
                     scroll-top-before)
           (scroll-fn scrolling-elem target-scroll-top)))))
   1))

(defn QuestionListFilters
  [{:keys [scope-filter
           set-scope-filter
           state-filter
           set-state-filter
           checked
           set-checked
           questionnaire-id
           current-section
           sections
           questionnaire
           questions]}]
  [:div.questions--filter-controls
   [:f> section-tabs {:questionnaire-id questionnaire-id
                      :current-section-id (:id current-section)
                      :sections sections
                      :loading  (-> questionnaire :sections :loading)
                      :on-tab-selected #(questions.db/set-filters
                                         questionnaire-id
                                         {"section" %})}]
   [:div.is-flex.is-align-items-center.has-background-tertiary.px-3.py-1
    [:input
     {:type "checkbox"
      :checked (= (count questions) (count checked))
      :on-change #(if (= (count questions) (count checked))
                    (set-checked #{})
                    (set-checked (->> questions
                                      (map :id)
                                      set)))}]

    [:div.ml-5.mr-3 "Selected"]
    [:f> answer-scope/AnswerScope
     {:on-commit #(rf/dispatch [::answers/set-answer-scope
                                questionnaire-id
                                checked
                                %])
      :hide-title true
      :disabled (not (seq checked))}]

    [:div.ml-3.px-4.py-2
     (when-not (= state-filter :all)
       {:class [:is-primary
                :no-pointer-events]})
     "Status"]
    [:select.input.ml-2
     {:value (name state-filter)
      :on-change #(-> % .-target .-value keyword set-state-filter)
      :style {:width "270px"}}
     [:option {:value "all"} "All Questions"]
     [:option {:value "unapproved"} "Unapproved Questions & Answers"]
     [:option {:value "In-review"} "In Review"]
     [:option {:value "approved"} "Approved"]
     [:option {:value "unknown"} "Unknown"]]

    [:div.ml-3.px-4.py-2
     (when-not (= scope-filter "all")
       {:class [:is-primary
                :no-pointer-events]})
     "Scope"]
    [:select.input.ml-2
     {:value scope-filter
      :on-change #(-> % .-target .-value set-scope-filter)
      :style {:width "270px"}}
     [:option {:value "all"} "All Scopes"]
     [:option {:value "Not yet evaluated"} "Not Yet Evaluated"]
     [:option {:value "Completed by Analyst"} "Completed by Analyst"]
     [:option {:value "Answer Provided, Client Reviewed"} "Answer Provided, Client Reviewed"]
     [:option {:value "Novel Information Needed"} "Novel Information Needed"]
     [:option {:value "Out of Scope"} "Out of Scope"]
     [:option {:value "No Answer Requested"} "No Answer Requested"]
     [:option {:value "Rescope requested"} "Rescope requested"]]]])

(defn handle-scroll-to
  [dom-element]
  (let [scrolling-elem (highest-dom-parent dom-element)]
    (js/setTimeout
     (fn []
       (scroll-fn scrolling-elem
                  (+ (-> dom-element .getBoundingClientRect .-top)
                     (.-scrollTop scrolling-elem)
                     -170)))
     1)))

(defn AnswerActions
  [on-update-answer question-ref question]
  (let [[active-action set-active-action] (react/useState :similar-questions)]
    [:f> popup-menu/popup-menu
     {:trigger-class [:button :is-secondary :mb-auto :is-rounded]
      :trigger-content [:div.p-0 [:i.icon.fas.fa-lightbulb]]
      :on-click (fn [_] (handle-scroll-to question-ref))
      :suspend-click-to-close true
      :menu-class :questions--answer-button-menu
      :disabled false
      :content
      [:<>
       [:div.questions--answer-button-menu-panel
        [:div.questions--answer-button-menu-panel-buttons.level-left.mt-0.mb-1
         [:div.pr-4
          [:h6.title.is-6.my-0
           [:i.icon.fas.fa-lightbulb.mr-2.is-5]
           "Build your Answer on"]]
         [:div.buttons
          [:button.button
           {:class (if (= active-action :similar-questions) :is-primary :is-secondary)
            :on-click (fn [e] (set-active-action :similar-questions))}
           "Similar Questions >"]
          [:button.button
           {:class (if (= active-action :similar-analyses) :is-primary :is-secondary)
            :on-click (fn [e] (set-active-action :similar-analyses))}
           "Similar Analyses with Associated Answers >"]]]
        (when active-action
          [:div.questions--answer-button-menu-action
           (if (= active-action :similar-analyses)
             [:f> recommend-analysis/recommend-analysis
              {:question question
               :on-submit on-update-answer}]
             [:f> recommend-question/recommend-questions
              {:question question
               :on-submit on-update-answer}])])]]}]))

(defn AnalysisActions
  [question]
  (let [[active-action set-active-action] (react/useState nil)]
    nil
    [:div {:style {:width "48px"}}]
    #_[:f> popup-menu/popup-menu
       {:trigger-class [:button :is-secondary :mb-auto :is-rounded]
        :trigger-content [:div.p-0 [:i.icon.fas.fa-shield-halved]]
        :suspend-click-to-close true
        :menu-class :questions--answer-button-menu
        :disabled false
        :content
        [:<> "Yo!"]}]))


(rf/reg-sub
 ::categories
 (fn [db [_]]
   (get-in db [:categories :data])))

(defn AssociatedAnswerAction
  [question on-update-answer action analysis menu-ref]
  (react/useEffect
   (fn []
     (let [dom-target (-> menu-ref
                          .-parentElement
                          .-parentElement)]
       (aset (.-style dom-target) "left" (str
                                          (- 50
                                             (.-x (.getBoundingClientRect dom-target)))
                                          "px"))
       (aset (.-style dom-target) "width" "calc(100vw - 100px)"))
     #js [action]))
  [:<>
   {:key (:construct-id analysis)}
   [:f> recommend-analysis/LinkedAnalysisAnswers
    {:question question
     :chosen-analysis analysis
     :on-submit on-update-answer}]])

(defn AnalysisFamily
  [question analysis all-analysis on-link-analysis menu-ref set-modal-instance]
  (let [[selected-version-ids set-selected-version-ids] (react/useState #{})
        [selected-construct-ids set-selected-construct-ids] (react/useState #{})]
    (react/useEffect
     (fn []
       (let [dom-target (-> menu-ref
                            .-parentElement
                            .-parentElement)]
         (aset (.-style dom-target) "left" (str
                                            (- 50
                                               (.-x (.getBoundingClientRect dom-target)))
                                            "px"))
         (aset (.-style dom-target) "width" "calc(100vw - 100px)")))
     #js [all-analysis])
    [:div
     [:div.level-left
      [:h4.title.is-6.my-0
       [:i.icon.fas.fa-sitemap.mr-2]
       "Link Analysis Family"]
      [:button.button.is-primary.ml-6
       {:on-click (fn []
                    (on-link-analysis
                     (->> all-analysis
                          (filter (comp (partial contains? selected-construct-ids) :construct-id))
                          first
                          :applied-analyses
                          (take 1)))
                    (when (fn? set-modal-instance)
                      (set-modal-instance (gensym))))}
       "Link Selected Analyses"]]
     [:f> analysis.tree/AnalysisTree
      {:loading false
       :analyses (vec all-analysis)
       :active-query true

       :hide-category true
       :hide-category-selection true


       :row-component analysis.tree/CheckboxAnalysisRow
       :path [:analysis-search :questions (:id question)]
       :category (-> analysis :categories first :name)
       :preselected-version-ids (->> question
                                     :selected-answer
                                     :applied-analyses
                                     (mapv :version-id)
                                     set)
       :search-result-construct-ids (analysis.tree/get-analysis-tree-displayed-construct-ids
                                     all-analysis
                                     #{(:construct-id analysis)})
       :selected-version-ids selected-version-ids
       :on-select (fn [{[{:keys [version-id construct-id] :as fml}] :applied-analyses}]
                    (set-selected-version-ids
                     ((if (contains? selected-version-ids version-id) disj conj)
                      selected-version-ids
                      version-id))
                    (set-selected-construct-ids
                     ((if (contains? selected-construct-ids construct-id) disj conj)
                      selected-construct-ids
                      construct-id)))}]]))

(defn LinkedAnalysisActions
  [{:keys [question
           all-analysis
           on-update-answer
           on-link-analysis
           on-unlink-analysis
           policy-process-pairs
           action
           analysis
           menu-ref
           set-modal-instance]}]
  (condp = action
    :answer-content
    (fn [] (AssociatedAnswerAction question on-update-answer action analysis menu-ref))

    :process-policy-pair
    (do
      (process<->policy/fetch-policy-process-pairs)
      (fn []
        (process<->policy/PolicyProcessPairs
         analysis
         policy-process-pairs
         (fn [arg]
           ((or set-modal-instance identity) (gensym))
           (on-link-analysis arg)))))

    :update-version
    (do
      (on-link-analysis
       (->> all-analysis
            (filter (comp (partial = (:construct-id analysis)) :construct-id))
            first
            :applied-analyses
            (take 1)))
      (on-unlink-analysis (:construct-id analysis)
                          (:version-id analysis)
                          identity))
    :view-analysis
    (do
      (analysis.view/show-editor-modal
       :view
       (:construct-id analysis)
       analysis)
      nil)

    :edit-analysis
    (do
      (analysis.view/show-editor-modal
       :edit
       (:construct-id analysis)
       analysis)
      nil)

    :analysis-family
    (fn [] (AnalysisFamily question analysis all-analysis on-link-analysis menu-ref set-modal-instance))

    (js/alert (str "Forgotten Key: " action))))

(defn QuestionHierarchy [{:keys [checked
                                 on-move-down
                                 on-move-up
                                 questions
                                 question-index
                                 q]}]
  (let [depth (:tree/depth q)
        down-question (-> questions
                          (->> (mapv #(assoc %2 :index %1) (range 0 (count questions)))
                               (filter
                                (fn [{:tree/keys [depth]
                                      :keys [index parent-id]}]
                                  (and (< index question-index)
                                       (= parent-id (:parent-id q))
                                       (= depth (:tree/depth q))))))
                          last
                          :id
                          (or (get-in questions [(dec question-index) :id])))]
    [:<>
     (if (zero? depth)
       [:div.questions--hierarchy-root-node
        (when-not (:suggestion q)
          [:div.questions--question-hierarchy-info
           [:div.questions--question-hierarchy-info-hover-data
            [:h6.title.is-6.my-1.level-left
             [:i.icon.fas.fa-bars-staggered.mr-2]
             "Question Hierarchy"
             [:button.button.is-rounded.is-circular.is-small.ml-3.is-secondary
              [:i.icon.fas.fa-chevron-left
               {:on-click (partial on-move-up q)}]]

             [:button.button.is-rounded.is-circular.is-small.is-secondary.ml-1
              {:on-click (partial on-move-down
                                  q
                                  down-question)
               :disabled (nil? down-question)}
              [:i.icon.fas.fa-chevron-right]]]
            [:div
             {:style {:color "#000"}}
             "- "
             (:display-name q)
             " - "
             (:content q)]]])
        (when (pos? (count (:tree/children q)))
          [:div.has-children])]
       (->> (range 0 depth)
            (mapv (fn [idx]
                    [:div.questions--hierarchy-spacer
                     (when (and (not (:suggestion q))
                                (= idx (dec depth)))
                       [:div.questions--question-hierarchy-info.nested
                        [:div.questions--question-hierarchy-info-horizontal-bar]
                        [:div.questions--question-hierarchy-info-hover-data
                         [:h6.title.is-6.my-1.level-left
                          [:i.icon.fas.fa-bars-staggered.mr-2]
                          "Question Hierarchy"
                          [:button.button.is-rounded.is-circular.is-small.ml-3.is-secondary
                           [:i.icon.fas.fa-chevron-left
                            {:on-click (partial on-move-up q)}]]
                          [:button.button.is-rounded.is-circular.is-small.is-secondary.ml-1
                           {:on-click (partial on-move-down q down-question)}
                           [:i.icon.fas.fa-chevron-right]]]
                         (->> (:tree/ancestor-nodes q)
                              (sort-by :ordinal)
                              (map-indexed
                               (fn [idx ancestor-q]
                                 [:div.is-flex
                                  [:div
                                   {:style {:margin-left (str (* idx 48)
                                                              "px")}}
                                   "-"]
                                  " "
                                  (:display-name ancestor-q)
                                  " - "
                                  (:content ancestor-q)
                                  [:hr.my-3]]))
                              (into [:<>]))
                         [:div.is-flex
                          {:style {:color "#000"}}
                          [:div
                           {:style {:margin-left (str (* depth 48)
                                                      "px")}} " - "]
                          " "
                          (:display-name q)
                          " - "
                          (:content q)]]])

                     (when (pos? (count (:tree/children q)))
                       [:div.has-children])

                     (when (-> questions
                               (get (inc question-index))
                               :tree/depth
                               (< depth)
                               (and (= idx (dec depth))))
                       (->> (range 0 (- depth
                                        (-> questions
                                            (get (inc question-index))
                                            :tree/depth)))
                            (mapv
                             (fn [i]
                               [:div.questions--question-hierarchy-info-block-last-edge
                                {:style {:margin-left (str
                                                       (* -52 i)
                                                       "px")}}]))
                            (into [:<>])))]))
            (into [:<>])))]))

(defn question-list-item [{:keys [all-analysis
                                  checked
                                  construct-id->categories
                                  features-enabled
                                  on-commit-edit
                                  on-link-analysis
                                  on-move-down
                                  on-move-up
                                  on-unlink-analysis
                                  on-update-answer
                                  process-policy-pairs
                                  questionnaire-id
                                  questions
                                  set-checked]} question-index q]
  [:div.questions-question-list--item
   {:class (when (checked (:id q)) :has-background-active)}
   [QuestionHierarchy
    {:checked checked
     :on-move-down on-move-down
     :on-move-up on-move-up
     :questions questions
     :question-index question-index
     :q q}]

   (if (:suggestion q)
     [:span.mr-5.ml-3.mt-5]
    [:input.mr-3.ml-3.mt-5
    {:type "checkbox"
     :checked (boolean (checked (:id q)))
     :on-change #(set-checked ((if (checked (:id q)) disj conj)
                               checked
                               (:id q)))}])
   [:div.is-flex-grow-1.px-4
    (when (:suggestion q)
      {:class :questions-question-list--suggestion})

    [:f> question.profile/Question
     {:features-enabled features-enabled
      :answer-actions-ui (partial AnswerActions (partial on-update-answer q))
      :analysis-actions-ui AnalysisActions
      :analysis-search-ui question.analysis.search/search-analysis-by-category-modal
      :question-lab-ui question-lab.view/question-lab-modal
      :comparison-symbol-ui comp.compare/ComparisonSymbol
      :construct->categories (comp #(get construct-id->categories %) :construct-id)
      :process-policy-pairs process-policy-pairs
      :on-scroll-to (partial handle-scroll-to)
      :on-update-scope #(rf/dispatch [::answers/set-answer-scope
                                      questionnaire-id
                                      #{(:id q)}
                                      %])
      :on-update-status #(questionnaire.db/update-question-status
                          questionnaire-id
                          (:id q)
                          %)
      :on-analysis-action (fn [action analysis menu-ref set-modal-instance too-many-args]
                            (when too-many-args (js/console.error "TOO MANY ARGS?!?!?!?"))
                            (LinkedAnalysisActions
                             {:question q
                              :all-analysis all-analysis
                              :on-update-answer (partial on-update-answer q)
                              :on-link-analysis (partial on-link-analysis
                                                         identity
                                                         questionnaire-id
                                                         (:id q))
                              :on-unlink-analysis (partial on-unlink-analysis
                                                           questionnaire-id
                                                           (:id q)
                                                           (-> q :selected-answer :id))
                              :policy-process-pairs process-policy-pairs
                              :action action
                              :analysis analysis
                              :menu-ref menu-ref
                              :set-modal-instance set-modal-instance}))
      :on-commit-edit on-commit-edit
      :on-click-construct (partial analysis.view/show-editor-modal :view)
      :on-click-state (fn [cid asa]
                        (analysis.view/show-editor-modal
                         (if (:next-version-id asa) :view :edit)
                         cid
                         asa))
      :on-link-analysis on-link-analysis
      :on-unlink-analysis on-unlink-analysis
      :on-report-analysis-suggestion (fn [construct]
                                       (questions.db/report-analysis-suggestion
                                        (:content q)
                                        construct
                                        false))
      :on-show-question-lab-modal #(rf/dispatch
                                    [::question-lab.events/show-question-lab-modal
                                     question-lab.view/question-lab-modal
                                     q
                                     @(questions.db/lab-question-ids* questionnaire-id)])}
     q]]])

(defn view [toggle-view]
  (let [{:keys [path]} @(rf/subscribe [::common-subs/route-parameters])

        questionnaire-id (-> path :questionnaire-id uuid)

        filters @(questions.db/filters* questionnaire-id)

        [status-frequencies total-questions] @(rf/subscribe [::questionnaires/question-status-frequencies questionnaire-id])

        sections @(rf/subscribe [::questionnaires/sections questionnaire-id])
        current-section (get filters "section")

        questionnaire @(rf/subscribe [::questionnaires/questionnaire questionnaire-id])

        loading-metadata? (-> questionnaire :metadata :loading)
        questionnaire-type (-> questionnaire :metadata :data :type)
        title (or (-> questionnaire :metadata :data :custom-name)
                  (-> questionnaire :metadata :data :filename))
        template-questionnaire? (-> questionnaire :metadata :data :template)

        features-enabled @(rf/subscribe [::common-subs/features-enabled])

        questions @(questions.db/questions* questionnaire-id)
        questions (vec questions)

        construct-id->categories @(analysis.db/construct-id->categories*)
        process-policy-pairs (or (process<->policy/fetch-policy-process-pairs)
                                 @(process<->policy/get-policy-process-pairs*))

        all-analysis @(analysis.search/all-analysis-structures)

        [state-filter set-state-filter] (react/useState :all)
        [scope-filter set-scope-filter] (react/useState "all")
        [checked set-checked] (react/useState #{})

        on-link-analysis (fn [callback questionnaire-id question-id analyses]
                           (analysis.db/link-analysis
                            (fn [& args]
                              (apply callback args)
                              (rf/dispatch [::ui/pop-modal]))
                            questionnaire-id
                            question-id
                            analyses))
        on-commit-edit (fn [question field-key value]
                         (when (str/blank? (:status question))
                           (questionnaire.db/update-question-status
                            questionnaire-id
                            (:id question)
                            "approved"))
                         (questions.db/commit-edit question field-key value))

        on-update-answer (fn [question coll answer-data overwrite-or-append]
                           (let [existing-response (question.profile/question->response question)
                                 existing-additional-information (question.profile/question->comment question)
                                 answer-data (assoc answer-data
                                                    :response
                                                    (if (= overwrite-or-append :append)
                                                      (str existing-response
                                                           (if (and (str/blank? existing-response)
                                                                    (not (str/blank? (:response answer-data))))
                                                             ""
                                                             "\n")
                                                           (:response answer-data))
                                                      (:response answer-data))
                                                    :additional-information
                                                    (if (= overwrite-or-append :append)
                                                      (str existing-additional-information
                                                           (if (and (str/blank? existing-additional-information)
                                                                    (not (str/blank? (:additional-information answer-data))))
                                                             ""
                                                             "\n")
                                                           (:additional-information answer-data))
                                                      (:additional-information answer-data)))]
                             (when (str/blank? (:status question))
                               (questionnaire.db/update-question-status
                                questionnaire-id
                                (:id question)
                                "approved"))
                             ; Clear Forms - all at once
                             (analysis.search/set-selected-question
                              [:analysis-search :questions (:id question) :recommended-question]
                              nil)
                             (analysis.search/set-selected-question
                              [:analysis-search :questions (:id question) :recommended-analysis]
                              nil)
                             (analysis.search/set-selected-analysis
                              [:analysis-search :questions (:id question) :recommended-analysis]
                              nil)

                             (questions.db/commit-update
                              (assoc question :selected-answer
                                     (-> question
                                         :selected-answer
                                         (merge answer-data)
                                         (assoc :applied-analyses (vec coll)))))))
        on-unlink-analysis (fn [questionnaire-id
                                question-id
                                answer-id
                                construct-id
                                version-id
                                callback]
                             (analysis.db/unlink-applied-analysis
                              questionnaire-id
                              question-id
                              answer-id
                              construct-id
                              version-id
                              callback))
        on-move (fn [question new-parent]
                  (questions.db/update-question
                   questionnaire-id
                   (:id question)
                   {:parent-id new-parent
                    :section-id (:section-id question)
                    :ordinal (:ordinal question)}
                   {:parent-id (:parent-id question)
                    :section-id (:section-id question)
                    :ordinal (:ordinal question)}
                   true))
        on-move-up  (fn [question]
                      (on-move question
                               (-> (:tree/ancestor-nodes question)
                                   (->> (sort-by :ordinal)
                                        (filter (comp (partial = (:parent-id question)) :id)))
                                   vec
                                   first
                                   :parent-id)))
        on-move-down (fn [question new-parent]
                       (on-move question new-parent))]
    [question.profile/question-wrapper
     [:div.level.my-0
      [breadcrumbs {:items (config/breadcrumbs {:id questionnaire-id
                                                :title title})}]
      [:button.button.is-tertiary.is-small.is-rounded.mt-2.mr-6.mb-0
       {:on-click #(toggle-view not)}
       "Table View"]]
     (when (and (contains? features-enabled :applied-analysis-creation)
                (not (-> questionnaire :questions :loading)))
       [:div.sticky.mx-6
        [progress.bar/progress-bar status-frequencies total-questions]])
     [:div.section.py-0.question-list
      [:div.section.py-4
       [:div.level
        [:div.level-left
         (if loading-metadata?
           [:div.loader]
           [title/title questionnaire-id (-> questionnaire :metadata :data)])]
        [:div.level-right
         (if loading-metadata?
           [:div.loader]
           (when (not template-questionnaire?)
             [questionnaire-status
              {:current-status (-> questionnaire :metadata :data :status)
               :on-change #(questions.db/update-questionnaire-status
                            {:questionnaire questionnaire-id
                             :status %})}]))]]]
      (if loading-metadata?
        [:div.loader]
        (when (and (contains? features-enabled :applied-analysis-creation)
                   (not template-questionnaire?))
          [:div.section.pt-0.pb-4.mt-0.mb-4
           [archives/archived-list questionnaire-type questionnaire-id]
           (when (not (#{"faq" "one-off"} questionnaire-type))
             [:<>
              [inference-jobs/inference-job-list questionnaire-id]])
           [:f> comp.compare/ComparisonControl questionnaire-id questions]]))]

     [QuestionListFilters
      {:scope-filter scope-filter
       :set-scope-filter set-scope-filter
       :state-filter state-filter
       :set-state-filter set-state-filter
       :checked checked
       :set-checked set-checked
       :questionnaire-id questionnaire-id
       :current-section current-section
       :sections sections
       :questionnaire questionnaire
       :questions questions}]

     [:div.section.pt-0.pb-6.questions--big-bottom
      (->> questions
           (filter (fn [q]
                     (cond
                       (= state-filter :In-review)
                       (= (:status q) "in-review")

                       (= state-filter :unknown)
                       (nil? (:status q))

                       (= state-filter :unapproved)
                       (not= (:status q) "approved")

                       (= state-filter :approved)
                       (= (:status q) "approved")
                       :else
                       true)))
           (filter (fn [q]
                     (cond
                       (not (#{"Out of Scope" "all"} scope-filter))
                       (-> q :answer-scope :content (= scope-filter))
                       (= scope-filter "all")
                       true
                       (= scope-filter "Out of Scope")
                       (str/includes? (-> q :answer-scope :content str/lower-case)
                                      "out of scope"))))
           (map-indexed
            (fn [question-index q]
              (question-list-item {:all-analysis all-analysis
                                   :checked checked
                                   :construct-id->categories construct-id->categories
                                   :features-enabled features-enabled
                                   :on-commit-edit on-commit-edit
                                   :on-link-analysis on-link-analysis
                                   :on-move-down on-move-down
                                   :on-move-up on-move-up
                                   :on-unlink-analysis on-unlink-analysis
                                   :on-update-answer on-update-answer
                                   :process-policy-pairs process-policy-pairs
                                   :questionnaire-id questionnaire-id
                                   :questions questions
                                   :set-checked set-checked}
                                  question-index
                                  q)))
           (into [:<>]))]]))
